/**
 * PeerConnection factory with perfect negotiation pattern,
 * ICE restart with exponential backoff, and connection state monitoring.
 */

import {
  ICE_SERVERS,
  ICE_RESTART_BACKOFF_MS,
  ICE_RESTART_MAX_ATTEMPTS,
  ICE_DISCONNECT_GRACE_MS,
  HEARTBEAT_TIMEOUT_MS,
  HEARTBEAT_DEAD_MS,
} from '../constants'
import { createDataChannels } from './datachannel'
import type { DataChannels } from './datachannel'

// ============================================================
// Types
// ============================================================

export type ConnectionQuality = 'good' | 'fair' | 'poor' | 'critical'

export interface QualitySnapshot {
  timestamp: number
  rtt: number | null
  availableBitrate: number | null
  packetLossRate: number
  jitter: number
  qualityLimitationReason: string | undefined
}

export interface PeerConnectionHandle {
  pc: RTCPeerConnection
  channels: DataChannels
  peerId: string
  polite: boolean
  /** Close the connection and clean up all resources */
  close(): void
  /** Add local media tracks to this connection (on Join click) */
  addTracks(stream: MediaStream): void
  /** Get the current connection quality classification */
  getQuality(): ConnectionQuality
  /** Get the latest quality snapshot */
  getQualitySnapshot(): QualitySnapshot | null
}

export interface PeerConnectionEvents {
  onTrack?: (peerId: string, event: RTCTrackEvent) => void
  onConnectionStateChange?: (peerId: string, state: RTCPeerConnectionState) => void
  onIceCandidate?: (peerId: string, candidate: RTCIceCandidate | null) => void
  onNegotiationNeeded?: (peerId: string, description: RTCSessionDescription) => void
  onChannelsReady?: (peerId: string, channels: DataChannels) => void
  onPeerTimeout?: (peerId: string) => void
  onPeerDead?: (peerId: string) => void
  onPeerLost?: (peerId: string) => void
}

// ============================================================
// Quality thresholds (from spec Section 6.2)
// ============================================================

const QUALITY_THRESHOLDS = {
  good: {
    minBitrate: 1_500_000,
    maxRtt: 0.150,
    maxPacketLoss: 0.02,
    maxJitter: 0.030,
  },
  fair: {
    minBitrate: 500_000,
    maxRtt: 0.300,
    maxPacketLoss: 0.05,
    maxJitter: 0.050,
  },
  poor: {
    minBitrate: 100_000,
    maxRtt: 0.500,
    maxPacketLoss: 0.10,
    maxJitter: 0.100,
  },
} as const

function classifyQuality(snapshot: QualitySnapshot): ConnectionQuality {
  for (const level of ['good', 'fair', 'poor'] as const) {
    const t = QUALITY_THRESHOLDS[level]
    if (
      (snapshot.availableBitrate === null || snapshot.availableBitrate >= t.minBitrate) &&
      (snapshot.rtt === null || snapshot.rtt <= t.maxRtt) &&
      snapshot.packetLossRate <= t.maxPacketLoss &&
      snapshot.jitter <= t.maxJitter
    ) {
      return level
    }
  }
  return 'critical'
}

// ============================================================
// PeerConnection factory
// ============================================================

/**
 * Create a PeerConnection with perfect negotiation, ICE restart,
 * and quality monitoring.
 *
 * Polite role: localPeerId < remotePeerId (lexicographic).
 * Connections are created data-only; call addTracks() on Join.
 */
export function createPeerConnection(
  localPeerId: string,
  remotePeerId: string,
  events: PeerConnectionEvents = {},
): PeerConnectionHandle {
  const polite = localPeerId < remotePeerId

  const pc = new RTCPeerConnection({
    iceServers: ICE_SERVERS as RTCIceServer[],
    bundlePolicy: 'max-bundle',
    rtcpMuxPolicy: 'require',
    iceCandidatePoolSize: 1,
  })

  // Create pre-negotiated DataChannels
  const channels = createDataChannels(pc)

  // Perfect negotiation state
  let makingOffer = false
  let ignoreOffer = false

  // ICE restart state
  let restartCount = 0
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null

  // Quality monitoring
  let qualityInterval: ReturnType<typeof setInterval> | null = null
  let prevStats: RawStats | null = null
  let latestSnapshot: QualitySnapshot | null = null

  // Heartbeat timeout tracking
  let heartbeatTimer: ReturnType<typeof setTimeout> | null = null
  let deadTimer: ReturnType<typeof setTimeout> | null = null

  // --- Perfect negotiation: outbound ---

  pc.onnegotiationneeded = async () => {
    try {
      makingOffer = true
      await pc.setLocalDescription()
      if (pc.localDescription) {
        events.onNegotiationNeeded?.(remotePeerId, pc.localDescription)
      }
    } catch (err) {
      console.error(`[webrtc] Negotiation failed for ${remotePeerId}:`, err)
    } finally {
      makingOffer = false
    }
  }

  // --- ICE candidate trickle ---

  pc.onicecandidate = ({ candidate }) => {
    events.onIceCandidate?.(remotePeerId, candidate)
  }

  // --- Remote tracks ---

  pc.ontrack = (event) => {
    events.onTrack?.(remotePeerId, event)
  }

  // --- Connection state machine ---

  pc.onconnectionstatechange = () => {
    const state = pc.connectionState
    events.onConnectionStateChange?.(remotePeerId, state)

    switch (state) {
      case 'connected':
        restartCount = 0
        clearReconnectTimer()
        startQualityMonitor()
        break

      case 'disconnected':
        clearReconnectTimer()
        reconnectTimer = setTimeout(() => {
          if (pc.connectionState === 'disconnected') {
            attemptRestart()
          }
        }, ICE_DISCONNECT_GRACE_MS)
        break

      case 'failed':
        attemptRestart()
        break

      case 'closed':
        clearReconnectTimer()
        stopQualityMonitor()
        break
    }
  }

  // --- DataChannel open notification ---

  let channelsOpenCount = 0
  const onChannelOpen = () => {
    channelsOpenCount++
    if (channelsOpenCount >= 3) {
      events.onChannelsReady?.(remotePeerId, channels)
    }
  }
  channels.signaling.addEventListener('open', onChannelOpen)
  channels.crypto.addEventListener('open', onChannelOpen)
  channels.health.addEventListener('open', onChannelOpen)

  // --- ICE restart with exponential backoff ---

  function attemptRestart(): void {
    clearReconnectTimer()

    if (restartCount >= ICE_RESTART_MAX_ATTEMPTS) {
      console.warn(`[webrtc] Max ICE restarts exceeded for ${remotePeerId}`)
      events.onPeerLost?.(remotePeerId)
      pc.close()
      return
    }

    const backoff = ICE_RESTART_BACKOFF_MS[restartCount] ?? ICE_RESTART_BACKOFF_MS[ICE_RESTART_BACKOFF_MS.length - 1]
    restartCount++

    reconnectTimer = setTimeout(() => {
      if (pc.connectionState !== 'connected' && pc.connectionState !== 'closed') {
        pc.restartIce()
        // restartIce() triggers onnegotiationneeded → perfect negotiation handles it
      }
    }, backoff)
  }

  function clearReconnectTimer(): void {
    if (reconnectTimer !== null) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }
  }

  // --- Perfect negotiation: inbound SDP handling ---

  async function handleRemoteDescription(description: RTCSessionDescriptionInit): Promise<void> {
    const offerCollision =
      description.type === 'offer' &&
      (makingOffer || pc.signalingState !== 'stable')

    ignoreOffer = !polite && offerCollision
    if (ignoreOffer) return

    await pc.setRemoteDescription(description)

    if (description.type === 'offer') {
      await pc.setLocalDescription()
      if (pc.localDescription) {
        events.onNegotiationNeeded?.(remotePeerId, pc.localDescription)
      }
    }
  }

  async function handleIceCandidate(candidate: RTCIceCandidateInit): Promise<void> {
    try {
      await pc.addIceCandidate(candidate)
    } catch (err) {
      if (!ignoreOffer) throw err
    }
  }

  // --- Quality monitoring ---

  interface RawStats {
    timestamp: number
    inbound: Array<{
      kind: string
      packetsReceived: number
      packetsLost: number
      bytesReceived: number
      jitter: number
    }>
    outbound: Array<{
      kind: string
      packetsSent: number
      bytesSent: number
      qualityLimitationReason?: string
    }>
    candidatePair: {
      currentRoundTripTime: number
      availableOutgoingBitrate: number
    } | null
  }

  function startQualityMonitor(): void {
    if (qualityInterval) return
    qualityInterval = setInterval(pollStats, 2_000)
  }

  function stopQualityMonitor(): void {
    if (qualityInterval) {
      clearInterval(qualityInterval)
      qualityInterval = null
    }
  }

  async function pollStats(): Promise<void> {
    try {
      const stats = await pc.getStats()
      const raw = extractRawStats(stats)

      if (prevStats) {
        latestSnapshot = computeSnapshot(prevStats, raw)
      }

      prevStats = raw
    } catch {
      // PC may be closed
    }
  }

  function extractRawStats(stats: RTCStatsReport): RawStats {
    const result: RawStats = {
      timestamp: Date.now(),
      inbound: [],
      outbound: [],
      candidatePair: null,
    }

    stats.forEach((report) => {
      switch (report.type) {
        case 'inbound-rtp':
          result.inbound.push({
            kind: report.kind,
            packetsReceived: report.packetsReceived ?? 0,
            packetsLost: report.packetsLost ?? 0,
            bytesReceived: report.bytesReceived ?? 0,
            jitter: report.jitter ?? 0,
          })
          break

        case 'outbound-rtp':
          result.outbound.push({
            kind: report.kind,
            packetsSent: report.packetsSent ?? 0,
            bytesSent: report.bytesSent ?? 0,
            qualityLimitationReason: report.qualityLimitationReason,
          })
          break

        case 'candidate-pair':
          if (report.state === 'succeeded') {
            result.candidatePair = {
              currentRoundTripTime: report.currentRoundTripTime ?? 0,
              availableOutgoingBitrate: report.availableOutgoingBitrate ?? 0,
            }
          }
          break
      }
    })

    return result
  }

  function computeSnapshot(prev: RawStats, curr: RawStats): QualitySnapshot {
    let totalPacketsLost = 0
    let totalPacketsReceived = 0
    let jitterMax = 0

    for (let i = 0; i < curr.inbound.length; i++) {
      const p = prev.inbound[i]
      const c = curr.inbound[i]
      if (p && c) {
        totalPacketsLost += c.packetsLost - p.packetsLost
        totalPacketsReceived += c.packetsReceived - p.packetsReceived
        jitterMax = Math.max(jitterMax, c.jitter)
      }
    }

    const packetLossRate =
      totalPacketsReceived > 0
        ? totalPacketsLost / (totalPacketsReceived + totalPacketsLost)
        : 0

    return {
      timestamp: curr.timestamp,
      rtt: curr.candidatePair?.currentRoundTripTime ?? null,
      availableBitrate: curr.candidatePair?.availableOutgoingBitrate ?? null,
      packetLossRate,
      jitter: jitterMax,
      qualityLimitationReason: curr.outbound.find((o) => o.kind === 'video')
        ?.qualityLimitationReason,
    }
  }

  // --- Heartbeat timeout tracking ---

  function resetHeartbeatTimer(): void {
    clearHeartbeatTimers()

    heartbeatTimer = setTimeout(() => {
      events.onPeerTimeout?.(remotePeerId)

      deadTimer = setTimeout(() => {
        events.onPeerDead?.(remotePeerId)
      }, HEARTBEAT_DEAD_MS - HEARTBEAT_TIMEOUT_MS)
    }, HEARTBEAT_TIMEOUT_MS)
  }

  function clearHeartbeatTimers(): void {
    if (heartbeatTimer !== null) {
      clearTimeout(heartbeatTimer)
      heartbeatTimer = null
    }
    if (deadTimer !== null) {
      clearTimeout(deadTimer)
      deadTimer = null
    }
  }

  // --- Track management ---

  function addTracks(stream: MediaStream): void {
    for (const track of stream.getTracks()) {
      pc.addTrack(track, stream)
    }
    // Each addTrack triggers onnegotiationneeded → perfect negotiation handles it
  }

  // --- Cleanup ---

  function close(): void {
    clearReconnectTimer()
    clearHeartbeatTimers()
    stopQualityMonitor()

    channels.signaling.close()
    channels.crypto.close()
    channels.health.close()

    pc.close()
  }

  const handle: PeerConnectionHandle = {
    pc,
    channels,
    peerId: remotePeerId,
    polite,
    close,
    addTracks,
    getQuality: () => (latestSnapshot ? classifyQuality(latestSnapshot) : 'good'),
    getQualitySnapshot: () => latestSnapshot,
  }

  // Attach inbound SDP/ICE handlers to the handle for external use
  ;(handle as PeerConnectionHandleInternal).handleRemoteDescription = handleRemoteDescription
  ;(handle as PeerConnectionHandleInternal).handleIceCandidate = handleIceCandidate
  ;(handle as PeerConnectionHandleInternal).resetHeartbeatTimer = resetHeartbeatTimer

  return handle
}

/**
 * Extended handle with internal methods used by the mesh manager.
 */
export interface PeerConnectionHandleInternal extends PeerConnectionHandle {
  handleRemoteDescription(description: RTCSessionDescriptionInit): Promise<void>
  handleIceCandidate(candidate: RTCIceCandidateInit): Promise<void>
  resetHeartbeatTimer(): void
}
